import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  ExternalLink,
  Eye,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  RotateCcw,
  Search,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import QueueRowDialog from "@/components/queue/QueueRowDialog";
import { AutoExtractPanel } from "@/components/queue/AutoExtractPanel";
import { cn } from "@/lib/utils";
import { CATEGORY_STYLES, STATUS_STYLES, inr, relTime } from "@/lib/format";
import { COLUMN_LABELS } from "@/lib/columns";
import {
  createQueueRow,
  deleteQueueRow,
  errMessage,
  listQueue,
  resetExportFlag,
  updateQueueRow,
} from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";

const FILTERS = [
  { key: "", label: "All" },
  { key: "ready", label: "Ready" },
  { key: "needs_review", label: "Needs Review" },
  { key: "exported", label: "Exported" },
];

const StatusBadge = ({ row }) => {
  const s = STATUS_STYLES[row.status] || STATUS_STYLES.needs_review;
  const issues = [...(row.missing_fields || []).map((f) => `${COLUMN_LABELS[f] || f} missing`), ...(row.invalid_fields || []).map((f) => `${COLUMN_LABELS[f] || f} invalid`)];
  return (
    <span
      className={cn("inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold", s.cls)}
      title={issues.join(" · ") || s.label}
      data-testid={`queue-status-${row.id}`}
    >
      {row.status === "ready" ? (
        <CheckCircle2 className="h-2.5 w-2.5" aria-hidden="true" />
      ) : row.status === "needs_review" ? (
        <AlertTriangle className="h-2.5 w-2.5" aria-hidden="true" />
      ) : (
        <Download className="h-2.5 w-2.5" aria-hidden="true" />
      )}
      {s.label}
    </span>
  );
};

export default function QueuePage() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [requiredFields, setRequiredFields] = useState([]);
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogSaving, setDialogSaving] = useState(false);

  const load = useCallback(
    async (opts = {}) => {
      setLoading(true);
      try {
        const data = await listQueue({
          status: opts.status ?? filter,
          q: opts.q ?? q,
        });
        setRows(data.items || []);
        setCounts(data.counts || {});
        setRequiredFields(data.required_fields || []);
      } catch (e) {
        toast.error("Could not load the queue", { description: errMessage(e) });
      } finally {
        setLoading(false);
      }
    },
    [filter, q]
  );

  // Background refresh used while the auto-extract worker is adding rows: no spinner,
  // no toast - the table just grows.
  const reloadSilently = useCallback(async () => {
    try {
      const data = await listQueue({ status: filter, q });
      setRows(data.items || []);
      setCounts(data.counts || {});
      setRequiredFields(data.required_fields || []);
    } catch (e) {
      /* ignored on purpose */
    }
  }, [filter, q]);

  useEffect(() => {
    load({ status: filter, q });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => {
    const t = setTimeout(() => load({ q }), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const patchRow = async (id, values) => {
    setSavingId(id);
    try {
      const updated = await updateQueueRow(id, values);
      setRows((prev) => prev.map((r) => (r.id === id ? updated : r)));
      load();
      return updated;
    } catch (e) {
      toast.error("Could not save the row", { description: errMessage(e) });
      return null;
    } finally {
      setSavingId(null);
    }
  };

  const onInlineCommit = async (row, key, value) => {
    if (!isAdmin) return;
    const before = row[key] ?? "";
    if (String(before) === String(value)) return;
    await patchRow(row.id, { [key]: value });
  };

  const addRow = async () => {
    try {
      const created = await createQueueRow({ company_name: "" });
      toast.success("Empty row added", { description: "Fill in the vendor details to make it Ready." });
      setRows((prev) => [created, ...prev]);
      setEditRow(created);
      setDialogOpen(true);
      load();
    } catch (e) {
      toast.error("Could not add a row", { description: errMessage(e) });
    }
  };

  const removeRow = async (id) => {
    try {
      await deleteQueueRow(id);
      setRows((prev) => prev.filter((r) => r.id !== id));
      toast.success("Row deleted");
      load();
    } catch (e) {
      toast.error("Could not delete", { description: errMessage(e) });
    }
  };

  const unexport = async (id) => {
    try {
      await resetExportFlag([id]);
      toast.success("Export mark cleared", { description: "The row can be exported again." });
      load();
    } catch (e) {
      toast.error("Could not update", { description: errMessage(e) });
    }
  };

  const saveDialog = async (draft) => {
    setDialogSaving(true);
    const values = {};
    Object.keys(draft || {}).forEach((k) => {
      if (
        ["id", "status", "missing_fields", "invalid_fields", "created_at", "updated_at", "exported_at", "source", "thread_id", "batch_id", "export_run_id", "extracted_at", "amount_is_approved", "amount_source", "thread_amount", "approval_source", "approved_evidence", "source_from", "source_date"].includes(k)
      )
        return;
      values[k] = draft[k];
    });
    const updated = await patchRow(draft.id, values);
    setDialogSaving(false);
    if (updated) {
      toast.success("Row saved", {
        description: updated.status === "ready" ? "Status: Ready to export" : "Status: Needs review",
      });
      setDialogOpen(false);
    }
  };

  const total = counts.all ?? rows.length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold tracking-tight sm:text-xl">Extraction queue</h1>
          <p className="text-xs text-muted-foreground sm:text-sm">
            Every extracted vendor row, from Gmail or manual uploads. Status is computed from your
            Required fields config.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" className="gap-1.5 bg-card" onClick={() => load()} data-testid="queue-refresh-button">
            <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} aria-hidden="true" /> Refresh
          </Button>
          {isAdmin ? (
            <>
              <Button variant="outline" size="sm" className="gap-1.5 bg-card" onClick={addRow} data-testid="queue-add-row-button">
                <Plus className="h-3.5 w-3.5" aria-hidden="true" /> Add row
              </Button>
              <Button asChild size="sm" className="gap-1.5">
                <Link to="/export" data-testid="queue-goto-export">
                  <Download className="h-3.5 w-3.5" aria-hidden="true" /> Go to export
                </Link>
              </Button>
            </>
          ) : (
            <span
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1 text-[11px] font-medium text-muted-foreground"
              data-testid="queue-readonly-chip"
            >
              <Eye className="h-3.5 w-3.5" aria-hidden="true" /> Read-only access
            </span>
          )}
        </div>
      </div>

      <AutoExtractPanel isAdmin={isAdmin} onRowsAdded={reloadSilently} />

      <div className="vde-card p-2.5">
        <div className="flex flex-col gap-2.5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-1.5">
            {FILTERS.map((f) => {
              const c = f.key === "" ? counts.all : counts[f.key];
              const active = filter === f.key;
              return (
                <button
                  key={f.key || "all"}
                  onClick={() => setFilter(f.key)}
                  data-testid={`queue-filter-${f.key || "all"}`}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12px] font-medium transition-colors",
                    active
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border bg-card hover:bg-secondary"
                  )}
                >
                  {f.label}
                  <span className={cn("vde-code rounded-full px-1.5 text-[10px]", active ? "bg-primary-foreground/20" : "bg-secondary")}>
                    {c ?? 0}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="relative w-full lg:w-80">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search vendor, PAN, A/c, approver…"
              className="h-9 bg-card pl-8 text-xs"
              data-testid="queue-search-input"
            />
          </div>
        </div>
      </div>

      {loading && !rows.length ? (
        <div className="vde-card flex items-center justify-center gap-2 p-10 text-xs text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading queue…
        </div>
      ) : null}

      {!loading && !rows.length ? (
        <div className="vde-card p-8 text-center" data-testid="queue-empty">
          <p className="text-sm font-semibold">
            {total ? "No rows in this filter" : "The queue is empty"}
          </p>
          <p className="mx-auto mt-1 max-w-md text-xs text-muted-foreground">
            Extract a mail from the Inbox, or run a Manual Extract and use “Send to queue”. You can
            also add an empty row and type the details in.
          </p>
          <div className="mt-4 flex justify-center gap-2">
            <Button asChild size="sm" variant="outline" className="bg-card">
              <Link to="/">Open inbox</Link>
            </Button>
            <Button asChild size="sm" variant="outline" className="bg-card">
              <Link to="/extract">Manual extract</Link>
            </Button>
            <Button size="sm" onClick={addRow} className="gap-1.5">
              <Plus className="h-3.5 w-3.5" aria-hidden="true" /> Add row
            </Button>
          </div>
        </div>
      ) : null}

      {rows.length ? (
        <>
          {/* desktop table */}
          <div className="vde-card hidden overflow-x-auto md:block">
            <table className="w-full min-w-[1000px] border-collapse text-left" data-testid="queue-table">
              <thead>
                <tr className="border-b border-border bg-secondary">
                  {["Status", "Vendor", "Amount", "Approver", "Source mail", "Actions"].map((h) => (
                    <th
                      key={h}
                      scope="col"
                      className="px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-foreground"
                      data-testid={`queue-th-${h.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const cat = CATEGORY_STYLES[row.category] || CATEGORY_STYLES.Other;
                  return (
                    <tr
                      key={row.id}
                      className="border-b border-border align-top hover:bg-secondary/50"
                      data-testid={`queue-row-${row.id}`}
                    >
                      <td className="px-3 py-2.5">
                        <StatusBadge row={row} />
                        {savingId === row.id ? (
                          <Loader2 className="ml-1 inline h-3 w-3 animate-spin text-muted-foreground" />
                        ) : null}
                        {(row.missing_fields || []).length || (row.invalid_fields || []).length ? (
                          <p className="mt-1 max-w-[150px] text-[10px] leading-snug text-warning-text">
                            {[...(row.invalid_fields || []).map((f) => `${COLUMN_LABELS[f] || f} invalid`), ...(row.missing_fields || []).map((f) => `${COLUMN_LABELS[f] || f} missing`)]
                              .slice(0, 3)
                              .join(", ")}
                          </p>
                        ) : null}
                      </td>

                      <td className="px-3 py-2">
                        <Input
                          defaultValue={row.company_name || ""}
                          onBlur={(e) => onInlineCommit(row, "company_name", e.target.value)}
                          placeholder="Company name"
                          readOnly={!isAdmin}
                          className="h-8 w-[220px] bg-card text-[12.5px] read-only:cursor-default read-only:bg-secondary"
                          data-testid={`queue-company-${row.id}`}
                        />
                        <div className="mt-1 flex items-center gap-1.5">
                          <Input
                            defaultValue={row.pan || ""}
                            onBlur={(e) => onInlineCommit(row, "pan", e.target.value)}
                            placeholder="PAN"
                            readOnly={!isAdmin}
                            className={cn(
                              "vde-code h-7 w-[120px] bg-card text-[11px] uppercase read-only:cursor-default read-only:bg-secondary",
                              (row.invalid_fields || []).includes("pan") && "border-danger-border bg-danger-surface"
                            )}
                            data-testid={`queue-pan-${row.id}`}
                          />
                          <Input
                            defaultValue={row.ifsc || ""}
                            onBlur={(e) => onInlineCommit(row, "ifsc", e.target.value)}
                            placeholder="IFSC"
                            readOnly={!isAdmin}
                            className={cn(
                              "vde-code h-7 w-[120px] bg-card text-[11px] uppercase read-only:cursor-default read-only:bg-secondary",
                              (row.invalid_fields || []).includes("ifsc") && "border-danger-border bg-danger-surface"
                            )}
                            data-testid={`queue-ifsc-${row.id}`}
                          />
                        </div>
                        <p className="vde-code mt-1 text-[10px] text-muted-foreground">
                          {row.account_number ? `A/c ${row.account_number}` : "A/c —"}
                          {row.bank_name ? ` · ${row.bank_name}` : ""}
                        </p>
                      </td>

                      <td className="px-3 py-2">
                        <Input
                          defaultValue={row.amount ?? ""}
                          onBlur={(e) => onInlineCommit(row, "amount", e.target.value)}
                          placeholder="0"
                          readOnly={!isAdmin}
                          className={cn(
                            "vde-code h-8 w-[130px] bg-card text-right text-[12.5px] read-only:cursor-default read-only:bg-secondary",
                            (row.missing_fields || []).includes("amount") && "border-warning-border bg-warning-surface"
                          )}
                          data-testid={`queue-amount-${row.id}`}
                        />
                        <p className="mt-1 text-[10px] text-muted-foreground">
                          {row.amount ? `₹${inr(row.amount)}` : "no amount"}
                          {row.amount_is_approved ? (
                            <span className="ml-1 font-semibold text-success-text">approved</span>
                          ) : null}
                        </p>
                        {row.amount_source ? (
                          <p
                            className="mt-0.5 text-[10px] text-muted-foreground"
                            title={
                              row.amount_source === "vendor_invoice_sum_approved"
                                ? `Per-vendor figure from this vendor's invoices; all rows add up to the approved ₹${inr(row.thread_amount)}`
                                : row.amount_source === "vendor_invoice"
                                  ? "Per-vendor figure read from this vendor's own invoices"
                                  : row.amount_source === "thread_approved"
                                    ? "Amount the approver signed off in the mail"
                                    : "Amount the requester asked for in the mail"
                            }
                            data-testid={`queue-amount-source-${row.id}`}
                          >
                            {row.amount_source === "thread_approved"
                              ? "from approval"
                              : row.amount_source === "thread_requested"
                                ? "from mail request"
                                : row.amount_source === "vendor_invoice_sum_approved"
                                  ? "from invoices · total approved"
                                  : "from invoices"}
                          </p>
                        ) : row.thread_amount ? (
                          <p
                            className="mt-0.5 text-[10px] text-warning-text"
                            title="This mail covers several vendors and only stated a combined total, so no amount was assumed for this row."
                            data-testid={`queue-amount-warning-${row.id}`}
                          >
                            combined total ₹{inr(row.thread_amount)} - enter this vendor's share
                          </p>
                        ) : null}
                      </td>

                      <td className="px-3 py-2">
                        <Input
                          defaultValue={row.approver_email || ""}
                          onBlur={(e) => onInlineCommit(row, "approver_email", e.target.value)}
                          placeholder="approver@company.com"
                          readOnly={!isAdmin}
                          className="vde-code h-8 w-[190px] bg-card text-[11.5px] read-only:cursor-default read-only:bg-secondary"
                          data-testid={`queue-approver-${row.id}`}
                        />
                        {row.approved_evidence ? (
                          <p className="mt-1 max-w-[190px] truncate text-[10px] italic text-muted-foreground" title={row.approved_evidence}>
                            “{row.approved_evidence}”
                          </p>
                        ) : null}
                      </td>

                      <td className="px-3 py-2.5">
                        <p className="max-w-[240px] truncate text-[12px] font-medium" title={row.source_subject || ""}>
                          {row.source_subject || (row.source === "manual" ? "Manual entry" : "—")}
                        </p>
                        <div className="mt-1 flex flex-wrap items-center gap-1.5">
                          {row.category ? (
                            <span className={cn("rounded-full border px-1.5 py-0.5 text-[9px] font-semibold", cat.badge)}>
                              {row.category}
                            </span>
                          ) : null}
                          <span className="text-[10px] text-muted-foreground">
                            {row.source === "gmail" ? "Gmail" : "Manual"} · {relTime(row.created_at)}
                          </span>
                          {row.source_link ? (
                            <a
                              href={row.source_link}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 text-[10px] font-medium text-primary hover:underline"
                              data-testid={`queue-source-link-${row.id}`}
                            >
                              <ExternalLink className="h-2.5 w-2.5" aria-hidden="true" /> Open thread
                            </a>
                          ) : null}
                        </div>
                      </td>

                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-1">
                          {isAdmin ? (
                            <>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 gap-1 bg-card px-2 text-[11px]"
                            onClick={() => {
                              setEditRow(row);
                              setDialogOpen(true);
                            }}
                            data-testid={`queue-edit-${row.id}`}
                          >
                            <Pencil className="h-3 w-3" aria-hidden="true" /> Edit all
                          </Button>
                          {row.status === "exported" ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 gap-1 px-2 text-[11px]"
                              onClick={() => unexport(row.id)}
                              data-testid={`queue-unexport-${row.id}`}
                            >
                              <RotateCcw className="h-3 w-3" aria-hidden="true" /> Un-export
                            </Button>
                          ) : null}
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0 text-muted-foreground hover:bg-danger-surface hover:text-danger-text"
                                aria-label="Delete row"
                                data-testid={`queue-delete-${row.id}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-card">
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete this queue row?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  {row.company_name || "This vendor row"} will be removed from the
                                  queue. The source mail stays in the inbox.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel data-testid="queue-delete-cancel">Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  onClick={() => removeRow(row.id)}
                                  data-testid="queue-delete-confirm"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                            </>
                          ) : (
                            <span className="text-[11px] text-muted-foreground" data-testid={`queue-readonly-${row.id}`}>
                              View only
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* mobile cards */}
          <div className="space-y-2 md:hidden">
            {rows.map((row) => (
              <div key={row.id} className="vde-card p-3" data-testid={`queue-card-${row.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <StatusBadge row={row} />
                  <span className="vde-code text-[10px] text-muted-foreground">
                    {relTime(row.created_at)}
                  </span>
                </div>
                <p className="mt-2 text-sm font-semibold">{row.company_name || "Unnamed vendor"}</p>
                <p className="vde-code mt-0.5 text-[11px] text-muted-foreground">
                  {row.pan || "PAN —"} · {row.ifsc || "IFSC —"} · {row.amount ? `₹${inr(row.amount)}` : "no amount"}
                </p>
                <p className="mt-1 truncate text-[11px] text-muted-foreground">
                  {row.source_subject || "Manual entry"}
                </p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {isAdmin ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 gap-1 bg-card px-2 text-[11px]"
                      onClick={() => {
                        setEditRow(row);
                        setDialogOpen(true);
                      }}
                      data-testid={`m-queue-edit-${row.id}`}
                    >
                      <Pencil className="h-3 w-3" aria-hidden="true" /> Edit
                    </Button>
                  ) : null}
                  {row.source_link ? (
                    <a
                      href={row.source_link}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex h-8 items-center gap-1 rounded-md border border-border bg-card px-2 text-[11px] font-medium"
                    >
                      <ExternalLink className="h-3 w-3" aria-hidden="true" /> Thread
                    </a>
                  ) : null}
                  {isAdmin ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="ml-auto h-8 w-8 p-0 text-muted-foreground"
                      onClick={() => removeRow(row.id)}
                      aria-label="Delete row"
                      data-testid={`m-queue-delete-${row.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                    </Button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </>
      ) : null}

      <QueueRowDialog
        row={editRow}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSave={saveDialog}
        saving={dialogSaving}
        requiredFields={requiredFields}
      />
    </div>
  );
}
